import { useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Paper, Radio, Stack, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { PlaceAutocomplete } from './PlaceAutocomplete';
import type { AssistantQuestion } from '../api/assistant';
import type { Poi } from '../api/types';

/**
 * What the assistant is waiting to be told, as a form rather than a paragraph.
 *
 * It used to ask in prose and read the answer out of whatever came back, and
 * two things came of that. What the operator had to type: "which of these five
 * Grand Hotels" is a question answered with "the second one", "no 2", "the
 * High Street one", or the name over again. And whether the answer could be
 * trusted: only the first of those resolves, and a round built from the wrong
 * one of five buildings is wrong in a way the map does not show. One answer
 * per click has neither problem.
 *
 * ONE AT A TIME, which is the other half of it. Four missing facts asked in a
 * single paragraph is a paragraph an operator answers two-thirds of, and the
 * next turn then asks for the rest and reads as though nobody was listening.
 *
 * There is always a box for an answer nobody offered. The choices are the
 * assistant's guess at what would be said, and a form that cannot take
 * "quarter past eight, from the depot" is a worse way of asking than the
 * paragraph was.
 */
export function AssistantQuestions({
  questions, cityId, onAnswer, onDismiss, busy,
}: {
  questions: AssistantQuestion[];
  /**
   * The city a place question searches. Without one there is nothing to
   * search, so the box is not offered and the typed answer stands instead.
   */
  cityId: string | null;
  /** Every answer as one message, in the order they were asked. */
  onAnswer: (message: string) => void;
  /** Put the form away and type instead. */
  onDismiss: () => void;
  busy?: boolean;
}) {
  /** Which question is on screen. */
  const [at, setAt] = useState(0);
  /**
   * What has been picked, per question, BY POSITION. Typed answers are kept
   * apart.
   *
   * Position rather than label, because the commonest question this form is
   * asked is "which of these two Grand Hotels" — where both options carry the
   * same words, and keying on them made one click select both and gave React
   * two children with one key.
   */
  const [picked, setPicked] = useState<Record<number, number[]>>({});
  const [typed, setTyped] = useState<Record<number, string>>({});
  /**
   * The place chosen for a place question, per question.
   *
   * Kept whole rather than as its name, because the id is the point: it is
   * what goes back to the assistant, and it is what stops "the Safeway"
   * having to be guessed at from among five of them.
   */
  const [place, setPlace] = useState<Record<number, Poi | null>>({});

  const question = questions[at];
  const chosen = picked[at] ?? [];
  const other = typed[at] ?? '';
  const last = at === questions.length - 1;

  /*
   * Whether this one has an answer yet.
   *
   * Typing counts, and counts INSTEAD: somebody who has written their own
   * answer has said the offered ones do not fit.
   */
  const picked_ = place[at] ?? null;
  /*
   * What kind of answer this wants.
   *
   * `search` was the first version of this and meant only "a place". It is
   * still read, so a service that has not been updated keeps working.
   */
  const kind = question?.answerType ?? (question?.search ? 'place' : 'choice');
  // A place question with no city has nothing to search, so it falls back to
  // being typed rather than showing a box that can only disappoint.
  const searching = kind === 'place' && Boolean(cityId);
  /*
   * Whether a field of its own is already holding the typed answer.
   *
   * It writes to the same place the free-text box does, so showing both would
   * be two inputs bound to one value — the second would blank the first.
   */
  const typedControl = kind === 'time' || kind === 'duration' || kind === 'number';
  const answered = other.trim().length > 0 || chosen.length > 0 || Boolean(picked_);

  /*
   * The three ways of answering are exclusive.
   *
   * An operator who searches out a destination has not also meant "return to
   * the start point", and sending both would leave the assistant to decide
   * which they meant — which is the guessing this form exists to remove.
   */
  const choosePlace = (poi: Poi | null) => {
    setPlace((current) => ({ ...current, [at]: poi }));
    if (poi) {
      setPicked((current) => ({ ...current, [at]: [] }));
      setTyped((current) => ({ ...current, [at]: '' }));
    }
  };

  /**
   * Whatever was typed, in whichever control typed it.
   *
   * One writer for all of them, because the three ways of answering are
   * exclusive: typing a time is not also picking the option beside it.
   */
  const sayIt = (value: string) => {
    setTyped((current) => ({ ...current, [at]: value }));
    if (value) {
      setPicked((current) => ({ ...current, [at]: [] }));
      setPlace((current) => ({ ...current, [at]: null }));
    }
  };

  /** Enter moves on, as it does in every field here. */
  const onEnter = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || e.shiftKey || !answered) return;
    e.preventDefault();
    if (last) onAnswer(message);
    else setAt(at + 1);
  };

  const choose = (index: number) => {
    setTyped((current) => ({ ...current, [at]: '' }));
    setPlace((current) => ({ ...current, [at]: null }));
    setPicked((current) => {
      const now = current[at] ?? [];
      if (!question.multiSelect) return { ...current, [at]: [index] };

      return {
        ...current,
        [at]: now.includes(index) ? now.filter((i) => i !== index) : [...now, index],
      };
    });
  };

  /**
   * The answers as one message.
   *
   * Headed by question, so the transcript reads as a set of answers rather
   * than a list of loose phrases and the assistant can tell which was which
   * when two of them are times. `answer` wins over `label` where they differ:
   * that is how a place choice sends back the NUMBER the assistant resolves
   * against the list it remembers, with the name along for readability.
   */
  const message = useMemo(() => {
    const parts: string[] = [];
    questions.forEach((q, index) => {
      const own = (typed[index] ?? '').trim();
      const found = place[index];
      /*
       * A searched place sends its NAME AND ITS ID.
       *
       * The name is for the transcript, which somebody reads. The id is what
       * the assistant passes to the planner, and is the whole reason the box
       * is here: a name it has to look up again is a name that can come back
       * as five Safeways, which is the question this form was built to stop
       * being asked twice.
       */
      const said = (found ? `${found.name} (place id ${found.id})` : '')
        || own
        || (picked[index] ?? [])
          .map((at_) => q.options[at_])
          .filter(Boolean)
          .map((option) => option.answer || option.label)
          .join(', ');
      if (said) parts.push(`${q.header}: ${said}`);
    });

    return parts.join('\n');
  }, [questions, picked, typed, place]);

  if (!question) return null;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, borderColor: 'primary.light', bgcolor: 'action.hover' }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Chip size="small" color="primary" variant="outlined" label={question.header} />
          {/* Said only when there is more than one, because "1 of 1" is noise. */}
          {questions.length > 1 && (
            <Typography variant="caption" color="text.secondary">
              {at + 1} of {questions.length}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" color="inherit" onClick={onDismiss} disabled={busy}>
            Type instead
          </Button>
        </Stack>

        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {question.question}
        </Typography>
        {question.multiSelect && (
          <Typography variant="caption" color="text.secondary">
            Pick as many as hold.
          </Typography>
        )}

        {/*
          The control that fits what is being asked.

          A question is not always a list of guesses. "What time do you set
          off" is a time, "how long have you got" is a number of hours, and
          "where does it start" is somewhere on the map with eleven thousand
          candidates — offering any of those as four buttons is offering four
          guesses, and the operator has to reject all of them to say the thing
          they actually meant.

          So the assistant says what KIND of answer it wants and this draws it:
          the planning form's own place search, a time field, a number of
          hours. Options still sit underneath as shortcuts where there are real
          ones to offer — likely times beside a time field, "Return to the
          start point" beside a place search.
        */}
        {kind === 'place' && cityId && (
          <PlaceAutocomplete
            cityId={cityId}
            label="Search for a place"
            value={picked_}
            onChange={choosePlace}
            disabled={busy}
          />
        )}

        {(kind === 'time' || kind === 'duration' || kind === 'number') && (
          <TextField
            size="small"
            type={kind === 'time' ? 'time' : 'number'}
            label={kind === 'time' ? 'Time' : kind === 'duration' ? 'Hours' : 'Number'}
            value={other}
            disabled={busy}
            autoFocus
            /*
              A duration is hours and a time is a clock, and neither is
              negative. The step on hours is a half, because half a day out is
              a thing people say and 3.5 hours is a thing they mean.
            */
            slotProps={{
              htmlInput: kind === 'time'
                ? {}
                : { min: 0, step: kind === 'duration' ? 0.5 : 1 },
              inputLabel: { shrink: true },
            }}
            onChange={(e) => sayIt(e.target.value)}
            onKeyDown={onEnter}
          />
        )}

        <Stack spacing={0.5}>
          {question.options.map((option, index) => {
            const on = chosen.includes(index);

            return (
              <Paper
                // By position: two places of one name are the reason this
                // form exists, so the label is not a key.
                key={index}
                variant="outlined"
                onClick={() => { if (!busy) choose(index); }}
                sx={{
                  px: 1, py: 0.5, cursor: busy ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'flex-start', gap: 0.5,
                  borderColor: on ? 'primary.main' : 'divider',
                  '&:hover': { borderColor: busy ? 'divider' : 'primary.main' },
                }}
              >
                {question.multiSelect
                  ? <Checkbox size="small" checked={on} sx={{ p: 0.25 }} />
                  : <Radio size="small" checked={on} sx={{ p: 0.25 }} />}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2">{option.label}</Typography>
                  {/*
                    What picking it means, and never behind a hover: this is
                    the line that tells two places of one name apart, so it is
                    what stops the wrong one being chosen.
                  */}
                  {option.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block' }}
                    >
                      {option.description}
                    </Typography>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Stack>

        {/*
          The way to say something nothing above offers.

          Left out only where a field of its own already holds what was typed
          — a time or a number writes to the same value, and two inputs on one
          value is the second blanking the first. Everywhere else it stays, and
          it is the whole answer where nothing else was offered: "where does
          the round start" has no shortlist unless somewhere was really looked
          up, and the assistant is told to ask with no options rather than to
          invent four buildings.
        */}
        {!typedControl && (
        <TextField
          size="small"
          placeholder={
            question.options.length > 0 || searching ? 'Or say it yourself' : 'Your answer'
          }
          autoFocus={question.options.length === 0 && !searching}
          value={other}
          disabled={busy}
          // Typing replaces a choice rather than adding to it.
          onChange={(e) => sayIt(e.target.value)}
          onKeyDown={onEnter}
        />
        )}

        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
          {/*
            Back, because a wrong answer two questions ago is otherwise only
            fixable by sending the lot and correcting it afterwards.
          */}
          {at > 0 && (
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => setAt(at - 1)}
              disabled={busy}
            >
              Back
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            disabled={busy || !answered}
            onClick={() => { if (last) onAnswer(message); else setAt(at + 1); }}
          >
            {last ? 'Send' : 'Next'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
